import { useCallback, type MouseEvent } from "react";

import { useEcommerceEvents } from "@portal/app/_components/hooks/useEcommerceEvents";
import { waitForJornayaLeadId } from "@portal/app/_components/hooks/useJornaya";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import {
  calculateLowestStartAmount,
  sumProgramCosts,
} from "@portal/app/_common/lib/programCostUtils";
import { getUrls } from "@portal/app/_common/lib/urls";
import type { DreamSearchViewerResult } from "@portal/app/_common/types/dreamSearch";
import type { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { useFormFunnelAttribution } from "@portal/app/health/_components/FormFunnelAttributionContext";

export function useDreamSearchResultNavigation(
  item: DreamSearchViewerResult | undefined,
  eventName: MixpanelEventName | null
) {
  const formFunnelAttribution = useFormFunnelAttribution();
  const { trackViewItem } = useEcommerceEvents();
  const analytics = useMixpanel();
  const isClickout = !!item?.program.preLeadRedirect && !item.program.allowHybridSignup;

  const getHref = useCallback(
    (jornayaLeadId?: string | null) =>
      item
        ? getUrls().DreamSearchClick({
            programId: item.program.id,
            isClickout,
            jornayaLeadId: jornayaLeadId ?? undefined,
            ...(formFunnelAttribution && { formFunnelId: formFunnelAttribution.formFunnelId }),
          })
        : "",
    [formFunnelAttribution, isClickout, item]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!item) return;

      trackViewItem(item);
      if (eventName) {
        void analytics.track(eventName, {
          programId: item.program.id,
          providerId: item.provider.id,
          providerName: item.provider.fullName,
          programName: item.program.name,
          label: item.label,
          score: item.score,
          preLeadRedirect: item.program.preLeadRedirect,
          preLeadRedirectParams: item.program.preLeadRedirectParams,
          allowHybridSignup: item.program.allowHybridSignup,
          cost: {
            estimatedFirstPayment: calculateLowestStartAmount(item),
            totalCost: sumProgramCosts(item.program.costs),
          },
          environment: item.program.environment,
        });
      }

      if (!isClickout) return;

      event.preventDefault();
      const targetWindow = window.open("about:blank", "_blank");
      void waitForJornayaLeadId().then((jornayaLeadId) => {
        const nextHref = getHref(jornayaLeadId);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.opener = null;
          targetWindow.location.href = nextHref;
        } else {
          window.location.assign(nextHref);
        }
      });
    },
    [analytics, eventName, getHref, isClickout, item, trackViewItem]
  );

  return {
    href: getHref(),
    isClickout,
    handleClick,
    canRequestInfo: !!item?.provider.isPartnered,
  };
}
